import * as React from "react";

import { appendManuals } from "components/products/Products";
import { filterProductManuals } from "components/support/support-and-help/ProductManuals";
import { filterSoftwarePackages } from "components/support/support-and-help/SoftwarePackages";
import { AUTH_LICENSE_VIEW_OWN } from "domain/authority";
import { LicenseData } from "domain/licenses";
import { buildOnboardingState, EMPTY_ONBOARDING_STATE, OnboardingState } from "domain/onboardings";
import { OnboardingImage, SoftwarePackage, SoftwarePackageHomePage } from "domain/softwarePackages";
import { licenseService } from "services/licenses/LicenseService";
import { onboardingsService } from "services/onboardings/OnboardingsService";
import { productManualsService } from "services/product-manuals/ProductManualsService";
import { softwarePackagesService } from "services/software-packages/SoftwarePackagesService";
import { getCurrentTenantDetails, hasTenantCookie, isInTenantAccess } from "services/tenants/tenantCookieService";
import { userSessionService } from "services/user/UserSessionService";

export interface Products {
    softwarePackages: SoftwarePackage[];
    licenses: LicenseData[];
    onboardingImages: OnboardingImage[];
    onboardingState: OnboardingState;
}

interface UseFilteredProductsState {
    products: Products | undefined;
    isLoading: boolean;
    error: Error | undefined;
    onOnboardingStateChange: (updater: (prev: OnboardingState) => OnboardingState) => void;
}

async function fetchOnboardingState(): Promise<OnboardingState> {
    if (isInTenantAccess()) {
        return EMPTY_ONBOARDING_STATE;
    }
    const onboardedProducts = await onboardingsService.fetchOnboardedProducts();
    return buildOnboardingState(onboardedProducts);
}

async function fetchProductsData(): Promise<Products | undefined> {
    if (!userSessionService.userHasAllAuthorities([AUTH_LICENSE_VIEW_OWN])) {
        return undefined;
    }

    const currentTenant = getCurrentTenantDetails();
    const [softwarePackagesData, licenseData, manualsData, onboardingState] = await Promise.all([
        softwarePackagesService.fetchSoftwarePackages(),
        licenseService.fetchAllLicenses({
            tenantIdentifier: currentTenant.uuid,
            own: !hasTenantCookie(),
        }),
        productManualsService.fetchProductManuals(),
        fetchOnboardingState(),
    ]);

    const filteredPackages = filterSoftwarePackages(
        softwarePackagesData.productDownloads,
        licenseData.licenses.licenseData
    );

    const filteredManuals = filterProductManuals(manualsData.productManuals, licenseData.licenses.licenseData);
    const packagesWithManuals = Array.from(
        appendManuals(new Set<SoftwarePackageHomePage>(filteredPackages), filteredManuals)
    );

    return {
        softwarePackages: packagesWithManuals,
        licenses: licenseData.licenses.licenseData,
        onboardingImages: softwarePackagesData.onboardingImages,
        onboardingState,
    };
}

export const useFilteredProducts = (isSoftwareFetchEnabled: boolean): UseFilteredProductsState => {
    const [products, setProducts] = React.useState<Products | undefined>(undefined);
    const [isLoading, setIsLoading] = React.useState(false);
    const [error, setError] = React.useState<Error | undefined>(undefined);
    const hasTriggeredFetch = React.useRef(false);

    React.useEffect(() => {
        if (!isSoftwareFetchEnabled || hasTriggeredFetch.current) {
            return;
        }
        hasTriggeredFetch.current = true;

        const fetch = async () => {
            setIsLoading(true);
            setError(undefined);
            try {
                const data = await fetchProductsData();
                setProducts(data);
            } catch (err) {
                setError(err instanceof Error ? err : new Error("Unknown error"));
            } finally {
                setIsLoading(false);
            }
        };

        void fetch();
    }, [isSoftwareFetchEnabled]);

    const onOnboardingStateChange = (updater: (prev: OnboardingState) => OnboardingState) => {
        setProducts((prev) => (prev ? { ...prev, onboardingState: updater(prev.onboardingState) } : prev));
    };

    return { products: products, isLoading, error, onOnboardingStateChange };
};
